import { CardElement } from '@stripe/react-stripe-js';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import useCheckoutState from '../../../hooks/useCheckoutState';
import PaymentElement from '../PaymentElement';
import Label from './Label';
import ExpressCheckoutElement from '../ExpressCheckoutElement';
import getBillingDetailsFromFields from '../../../lib/getBillingDetailsFromFields';
import getFormFieldValues from '../../../lib/getFormFieldValues';
import clsx from 'clsx';
import { get, uniqueId } from 'lodash';
import { FocusProvider } from '../../../contexts/FocusContext';
import FocusableInput from './FormField/FocusableInput';
import useEffectiveColorMode from '../../../hooks/useEffectiveColorMode';
import {
  buildFontFamilyWithFallbacks,
  convertFontSizeForStripe,
} from '@repo/checkout/lib/fontUtils';
import StyledElement from '@repo/checkout/components/StyledElement';
import { getFontBodySizes, INPUT_PLACEHOLDER_COLOR } from '@repo/checkout/components/CheckoutCSS';
import getThemeLabels from '@repo/checkout/lib/getThemeLabels';

const defaultColor = {
  light: {
    textPrimary: '#000000',
    colorPrimary: '#000000',
    inputColor: '#1f2937',
    inputBorderColor: '#f0eeee',
    inputBorderColorFocus: '#f0eeee',
    placeholderColor: INPUT_PLACEHOLDER_COLOR.light,
  },
  dark: {
    textPrimary: '#ffffff',
    colorPrimary: '#ffffff',
    inputColor: '#f5f5f5',
    inputBorderColor: '#ffffff',
    inputBorderColorFocus: '#ffffff',
    placeholderColor: INPUT_PLACEHOLDER_COLOR.dark,
  },
};

const getInputShadowStyle = shadowSetting => {
  const shadowStyleMap = {
    none: 'none',
    sm: `0 1px 2px 0 rgba(0, 0, 0, 0.05)`,
    md: `0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)`,
    lg: `0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)`,
    xl: `0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)`,
  };
  return shadowStyleMap[shadowSetting] || shadowStyleMap.sm;
};

export const getCardFieldStylesFromTheme = (theme, colorMode) => {
  const fontMain = theme?.tokens?.fontMain;
  const fontBody = theme?.tokens?.fontBody;
  const effectiveBodyFamily = fontBody?.family || fontMain?.family;
  const textPrimary =
    theme?.tokens?.textPrimary?.[colorMode] || defaultColor[colorMode].textPrimary;
  const colorPrimary =
    theme?.tokens?.colorPrimary?.[colorMode] || defaultColor[colorMode].colorPrimary;

  const fontBodyWeight = fontBody?.weight || 400;

  // Get element styles from theme
  const formFieldLabelStyles = get(theme.elements, `checkout.form.fields.label.styles`);
  const formFieldLabelFocusStyles = get(theme.elements, `checkout.form.fields.label.focusStyles`);
  const formFieldInputStyles = get(theme.elements, `checkout.form.fields.input.styles`);

  const formFieldInputPlaceholder = get(theme.elements, `checkout.form.fields.input.placeholder`);
  const formFieldInputPlaceholderStyles = get(
    theme.elements,
    `checkout.form.fields.input.placeholderStyles`,
  );

  const placeholderEnabled =
    typeof formFieldInputPlaceholder?.enabled === 'boolean'
      ? formFieldInputPlaceholder.enabled
      : true;

  // Get selected styles from theme if applicable
  const formFieldInputSelectedStyles = get(
    theme.elements,
    `checkout.form.fields.input.selectedStyles`,
  );

  // Get focus styles from theme if applicable
  const formFieldInputFocusStyles = get(theme.elements, `checkout.form.fields.input.focusStyles`);

  const fontBodySizes = getFontBodySizes(theme?.tokens);

  // Stripe Elements iframe can't see our SSR-emitted "<Family> Fallback" rule.
  const stripeFontOptions = { includeAdjustedFallback: false };
  const baseFontFamily = buildFontFamilyWithFallbacks(effectiveBodyFamily, [], stripeFontOptions);
  const inputFontFamily = buildFontFamilyWithFallbacks(
    formFieldInputStyles?.fontFamily || effectiveBodyFamily,
    [],
    stripeFontOptions,
  );
  const labelFontFamily = buildFontFamilyWithFallbacks(
    formFieldLabelStyles?.fontFamily || effectiveBodyFamily,
    [],
    stripeFontOptions,
  );

  // Convert rem values to pixels for Stripe Elements (they run in iframe with different root font-size)
  const fontSizeForStripe = convertFontSizeForStripe(
    formFieldInputStyles?.fontSize || fontBodySizes.base?.fontSize,
  );

  // Calculate focus ring color (similar to CheckoutCSS.js logic)
  const focusRingColor =
    formFieldInputFocusStyles?.borderColor?.[colorMode] ||
    formFieldInputStyles?.borderColor?.[colorMode] ||
    defaultColor[colorMode].inputBorderColorFocus;

  // for CardElement, other styles are set on the wrapping element
  // via CheckoutCSS.js on .cp_stripe-card-element
  const inputShadow = getInputShadowStyle(formFieldInputStyles?.boxShadow);
  const cardFieldStyles = {
    lineHeight: formFieldInputStyles?.lineHeight || 1.5,
    color: formFieldInputStyles?.color?.[colorMode] || defaultColor[colorMode].inputColor,
    fontWeight: formFieldInputStyles?.fontWeight || fontBodyWeight || 400,
    fontFamily: inputFontFamily,
    fontSize: fontSizeForStripe,
    fontSmoothing: 'antialiased',
    boxShadow: inputShadow,
    padding: `${formFieldInputStyles?.paddingY ? `${formFieldInputStyles?.paddingY}px` : '0.675rem'} ${formFieldInputStyles?.paddingX ? `${formFieldInputStyles?.paddingX}px` : '0.675rem'}`,
    ':focus': {
      color:
        formFieldInputFocusStyles?.color?.[colorMode] ||
        formFieldInputStyles?.color?.[colorMode] ||
        defaultColor[colorMode].inputColor,
      boxShadow:
        formFieldInputStyles?.boxShadow === 'none'
          ? `inset 0 0 0 1px ${focusRingColor}`
          : `inset 0 0 0 1px ${focusRingColor}, ${inputShadow}`,
    },
    '::placeholder': {
      color: placeholderEnabled
        ? formFieldInputPlaceholderStyles?.color?.[colorMode] ||
          defaultColor[colorMode].placeholderColor
        : 'transparent',
      fontFamily: inputFontFamily,
      fontSize: fontSizeForStripe,
      fontWeight: formFieldInputStyles?.fontWeight || fontBodyWeight || 400,
      fontStyle: formFieldInputStyles?.fontStyle || 'normal',
    },
    '::focus-visible': {
      outline: 'none',
    },
  };

  // Forward extra properties from field input styles that aren't explicitly
  // handled above (e.g. letterSpacing, textTransform from legacy cardFieldStyles)
  const handledInputProps = new Set([
    'color',
    'fontSize',
    'fontWeight',
    'fontFamily',
    'fontStyle',
    'boxShadow',
    'paddingX',
    'paddingY',
    'borderColor',
    'borderWidth',
    'borderRadius',
    'backgroundColor',
    'boxShadowColor',
    'lineHeight',
  ]);
  if (formFieldInputStyles) {
    for (const [key, value] of Object.entries(formFieldInputStyles)) {
      if (!handledInputProps.has(key) && value !== undefined) {
        cardFieldStyles[key] = value;
      }
    }
  }

  // Forward extra placeholder properties
  if (formFieldInputPlaceholderStyles) {
    const handledPlaceholderProps = new Set(['color']);
    for (const [key, value] of Object.entries(formFieldInputPlaceholderStyles)) {
      if (!handledPlaceholderProps.has(key) && value !== undefined) {
        cardFieldStyles['::placeholder'][key] = value;
      }
    }
  }

  // Forward extra focus properties
  if (formFieldInputFocusStyles) {
    const handledFocusProps = new Set(['color', 'borderColor', 'backgroundColor']);
    for (const [key, value] of Object.entries(formFieldInputFocusStyles)) {
      if (!handledFocusProps.has(key) && value !== undefined) {
        cardFieldStyles[':focus'][key] = value;
      }
    }
  }

  return cardFieldStyles;
};

export const createPaymentMethodStyle = (theme, colorMode) => {
  const cardFieldStyles = getCardFieldStylesFromTheme(theme, colorMode);

  // The card brand icon sits next to the placeholder, so it takes the same
  // colour. A disabled placeholder is transparent, which would hide the icon —
  // fall back to the default colour in that case.
  const placeholderColor = cardFieldStyles['::placeholder']?.color;
  const iconColor =
    placeholderColor && placeholderColor !== 'transparent'
      ? placeholderColor
      : defaultColor[colorMode].placeholderColor;

  return {
    style: {
      base: {
        iconColor,
        '::focus-visible': {
          outline: 'none',
        },
        ...cardFieldStyles,
      },
    },
  };
};

export function getPaymentFormType({ page, seller, isScreenshot, isMeasurer }) {
  const paymentMethods = page.paymentMethods.stripe;

  if (paymentMethods.multiple?.enabled === true) {
    return isMeasurer ? null : 'payment';
  }

  if (!seller.paymentGateways?.stripe?.enableAppleGooglePay) {
    return 'card';
  }

  if (
    paymentMethods.card?.enabled === true &&
    paymentMethods.agpay?.enabled === true &&
    !isScreenshot
  ) {
    return paymentMethods.agpay.mode === 'express' ? 'card' : 'card-agpay';
  }

  return paymentMethods.card?.enabled === true ? 'card' : null;
}

const PaymentMethods = ({ onReady }) => {
  const { page, seller, isScreenshot, isMeasurer } = useCheckoutState();

  switch (getPaymentFormType({ page, seller, isScreenshot, isMeasurer })) {
    case 'payment':
      return <MultiplePaymentMethods onReady={onReady} />;
    case 'card':
      return <CardPaymentMethod onReady={onReady} />;
    case 'card-agpay':
      return <CardAgpayPaymentMethod onReady={onReady} />;
    default:
      return null;
  }
};

function CardAgpayPaymentMethod({ onReady }) {
  const { isMeasurer } = useCheckoutState();

  return (
    <>
      <div
        className={clsx(
          'cp_payment-method cp_stripe-express-checkout-element-wrapper',
          'bg-[var(--cp_checkout-form-expressCheckout-stripeExpressCheckoutElement-background-color)]',
          'text-[var(--cp_checkout-form-expressCheckout-stripeExpressCheckoutElement-color)]',
        )}
      >
        {!isMeasurer && <ExpressCheckoutElement mode="default" />}
      </div>

      <div
        className={clsx(
          'cp_payment-method-wrapper cp_stripe-card-element-wrapper',
          'bg-[var(--cp_checkout-form-stripePaymentElement-background-color)]',
        )}
      >
        <CardPaymentMethod onReady={onReady} />
      </div>
    </>
  );
}

function CardPaymentMethod({ onReady }) {
  const { page, form, isTesting, theme, setFormError } = useCheckoutState();
  const [isCardElementReady, setIsCardElementReady] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const containerRef = useRef(null);
  const labels = getThemeLabels(theme);

  const { fields } = getFormFieldValues(form.fields, page.fields);
  const billingDetails = getBillingDetailsFromFields(fields);

  const hasPostalCodeInAddress = !!billingDetails?.address?.postal_code;
  const hidePostalCode = hasPostalCodeInAddress || !!isTesting;

  const colorMode = useEffectiveColorMode();

  const themeKey = JSON.stringify({
    fontBody: theme?.tokens?.fontBody,
    inputFontFamily: theme?.elements?.checkout?.form?.fields?.input?.styles?.fontFamily,
    labelFontFamily: theme?.elements?.checkout?.form?.fields?.label?.styles?.fontFamily,
    colorMode,
  });
  const key = useMemo(() => {
    return uniqueId('stripe-card-element-');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- themeKey triggers key regeneration when theme values change
  }, [themeKey]);

  // Lock iframe height after mount so Stripe's element.update() can't shrink it.
  // A CSS rule in checkout.css uses this variable with !important to override Stripe's inline height.
  useEffect(() => {
    if (!isCardElementReady || !containerRef.current) return;
    const iframe = containerRef.current.querySelector('iframe');
    if (iframe) {
      requestAnimationFrame(() => {
        if (iframe.isConnected) {
          containerRef.current?.style.setProperty(
            '--cp-stripe-iframe-min-height',
            `${iframe.getBoundingClientRect().height}px`,
          );
        }
      });
    }
  }, [isCardElementReady, key]);

  // Clear locked height on remount so the new CardElement can establish its own height
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.style.removeProperty('--cp-stripe-iframe-min-height');
    }
  }, [key]);

  return (
    <FocusProvider groupId={`stripe-card-element`}>
      <StyledElement
        as={Label}
        elementPath="checkout.form.fields.label"
        htmlFor={`stripe-card-element-${page._id}`}
        className={clsx('cp_form-label cp_payment-method-label')}
        required
      >
        {labels.cardLabelText}
      </StyledElement>

      <div
        ref={containerRef}
        className="cp_payment-method cp_stripe-card-element-container mt-1 relative"
      >
        <FocusableInput elementId={`stripe-card-element-${page._id}`}>
          <CardElement
            key={key}
            onReady={element => {
              setIsCardElementReady(true);
              onReady?.(element);
            }}
            onChange={() => setFormError(null)}
            id={`stripe-card-element-${page._id}`}
            options={{
              ...createPaymentMethodStyle(theme, colorMode),
              hidePostalCode,
              disableLink: true,
            }}
            onFocus={() => {
              setIsFocused(true);
            }}
            onBlur={() => {
              setIsFocused(false);
            }}
            className={clsx(
              'cp_stripe-card-element placeholder-gray-200 appearance-none block w-full h-auto',
              {
                focused: isFocused,
              },
            )}
          />
        </FocusableInput>

        {!isCardElementReady && (
          <div
            className={clsx(
              'cp_stripe-card-element-loader absolute top-0 left-0 w-full h-full flex items-center justify-between pointer-events-none',
              'bg-[var(--cp_checkout-form-stripePaymentElement-background-color)]',
            )}
          >
            <div className="flex items-center gap-2">
              <div
                className={clsx(
                  'ml-2 h-4 w-6 rounded animate-pulse blur-[4px] opacity-30',
                  'bg-[var(--cp_checkout-form-fields-input-placeholder-color)]',
                )}
              ></div>
              <div
                className={clsx(
                  'h-4 w-24 rounded animate-pulse blur-[4px] opacity-30',
                  'bg-[var(--cp_checkout-form-fields-input-placeholder-color)]',
                )}
              ></div>
            </div>
            <div
              className={clsx(
                'mr-6 h-4 w-28 rounded animate-pulse blur-[4px] opacity-30',
                'bg-[var(--cp_checkout-form-fields-input-placeholder-color)]',
              )}
            ></div>
          </div>
        )}
      </div>
    </FocusProvider>
  );
}

function MultiplePaymentMethods({ onReady }) {
  return <PaymentElement onReady={onReady} />;
}

export default PaymentMethods;
